import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { filterSelectedPosesForPicker } from '../../../../../../utils/posePicker';
import PoseEntry from './PoseEntry';
import { getImageRecords, productIsAllGroup } from '../../../imageRecords';
import NoPoseImageMessage from '../../../NoPoseImageMessage';
import {
  advanceConfigSlot,
  setPoseSelectionIds,
  setPoseSelections,
} from '../../../../../../reducers/productConfigSlice';
import { selectTransformedPoses } from '../../../../../../selectors';

const PosesList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  justify-content: center;
  padding-left: 0;

  @media (max-width: 768px) {
    gap: 14px;
    justify-content: center;
  }
`;

const PosesConfig = () => {
  const dispatch = useDispatch();
  const isPrepay = useSelector((state: any) => state.shoot.isPrepay);
  const activeConfigSlotIndex = useSelector(
    (state: any) => state.productConfig.activeConfigSlotIndex,
  );
  const activeConfigStep = useSelector(
    (state: any) => state.productConfig.activeConfigStep,
  );
  const poseSelectionIds = useSelector(
    (state: any) => state.productConfig.poseSelectionIds,
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const poseSelections: any[] = useSelector(
    (state: any) => state.productConfig.poseSelections,
  );
  const { allAvailablePosesForOrder, availablePoses, studentGallery } =
    useSelector(selectTransformedPoses);

  const hideSelectedPosesInOrderUi = useSelector(
    (state: any) => state.additionalData?.hideSelectedPosesInOrderUi,
  );
  const product = useSelector(
    (state: any) => state.productConfig.currentProduct,
  );
  // Pose slots come from the universal records list, not the raw poses_count —
  // so multiple-image products expose one slot per node.
  const records = getImageRecords(product);
  const posesCount: number = records.length;

  const addPoseSelection = (id: any, cropIndex: number | null = null) => {
    const updatedSelectionIds = [...poseSelectionIds];
    const updatedSelections = [...poseSelections];
    const targetIndex =
      activeConfigSlotIndex !== null && activeConfigSlotIndex !== undefined
        ? activeConfigSlotIndex
        : updatedSelectionIds.length < posesCount
        ? updatedSelectionIds.length
        : Math.max(posesCount - 1, 0);

    let availablePosesToSelect: any[] =
      posesCount > 1
        ? (availablePoses as any[])
        : (studentGallery as any[])?.length > 0
        ? (studentGallery as any[])
        : (availablePoses as any[]);

    if ((allAvailablePosesForOrder as any[])?.length > 0) {
      availablePosesToSelect = allAvailablePosesForOrder as any[];
    }

    const selectedPose: any = {
      ...availablePosesToSelect?.find((pose: any) => pose.id === id),
    };

    if (cropIndex !== null) {
      selectedPose.selected_crop = selectedPose.cropped_images?.[cropIndex];
    }

    updatedSelectionIds[targetIndex] = id;
    updatedSelections[targetIndex] = selectedPose;

    while (
      updatedSelectionIds.length > 0 &&
      updatedSelectionIds[updatedSelectionIds.length - 1] === undefined
    ) {
      updatedSelectionIds.pop();
      updatedSelections.pop();
    }

    dispatch(setPoseSelectionIds(updatedSelectionIds as number[]));
    dispatch(setPoseSelections(updatedSelections));

    // advanceConfigSlot reads the selections dispatched just above from the
    // store (dispatch is synchronous), advances to the next unfilled slot,
    // or closes the panel when configuration is complete.
    (dispatch as any)(
      advanceConfigSlot({ slotIndex: targetIndex, step: 'pose' }),
    );
  };

  if (!product) return null;

  // Poses only render for an explicitly seeded 'pose' step, mirroring the
  // 'background' guard in BackgroundsConfig. A visible panel always has a
  // step — openConfigSlot pairs slot, step, and visibility in the reducer.
  if (activeConfigStep !== 'pose') {
    return null;
  }

  const hasActiveConfigSlot =
    activeConfigSlotIndex !== null && activeConfigSlotIndex !== undefined;

  const hasPoseOptions =
    (availablePoses as unknown[])?.length > 0 ||
    (studentGallery as unknown[])?.length > 0 ||
    (allAvailablePosesForOrder as unknown[])?.length > 0;

  // The active slot's pose source is empty, so there is nothing to pick — say
  // which kind of photo is missing instead of rendering an empty picker.
  // selectTransformedPoses has already resolved the source for THIS slot (group
  // slots read studentGroupImages and deliberately never fall back to the
  // individual gallery), so hasPoseOptions is per slot and the message follows
  // the slot's own record type. The navigator skips unfillable slots, so this
  // is reached by tapping such a slot's tile directly.
  if (posesCount > 0 && !isPrepay && !hasPoseOptions) {
    // Without an active slot, fall back to the product's own shape: an
    // all-group product can only be missing a group photo.
    const slotIsGroup = hasActiveConfigSlot
      ? records[activeConfigSlotIndex]?.type === 'group'
      : productIsAllGroup(product);

    return <NoPoseImageMessage slotIsGroup={slotIsGroup} />;
  }

  // Prepay orders never offer pose selection, even if a stale pose step is
  // active or pose data is present.
  if (!hasPoseOptions || isPrepay) {
    return null;
  }

  // Prioritize availablePoses when posesCount > 1, otherwise prioritize non-empty studentGallery, fallback to availablePoses if studentGallery is empty or non-existent.
  let poseSource =
    posesCount > 1
      ? availablePoses
      : studentGallery && (studentGallery as unknown[])?.length > 0
      ? studentGallery
      : availablePoses;

  if ((allAvailablePosesForOrder as unknown[])?.length > 0) {
    poseSource = allAvailablePosesForOrder;
  }

  const selectedIdsForPicker = !hasActiveConfigSlot
    ? poseSelectionIds
    : poseSelectionIds.filter(
        (_: number, index: number) => index !== activeConfigSlotIndex,
      );

  poseSource = filterSelectedPosesForPicker(
    poseSource ?? [],
    selectedIdsForPicker,
    posesCount,
    hideSelectedPosesInOrderUi,
  );

  const selectedCropIds = new Set(
    poseSelections
      .filter((p) => p?.selected_crop?.id)
      .map((p) => p.selected_crop.id),
  );

  // TODO: handle group image type
  const poses = (poseSource as any[])?.map((pose) => (
    <PoseEntry
      key={pose.id}
      pose={pose}
      poseSelectionIds={poseSelectionIds as unknown[]}
      selectedCropIds={selectedCropIds}
      onSelect={(id, cropIndex = null) => {
        addPoseSelection(id, cropIndex);
      }}
    />
  ));

  return (
    <div className="bnl-product-config__poses">
      <PosesList>{poses}</PosesList>
    </div>
  );
};

export default PosesConfig;
